import React from "react";
import RatingScale from "./RatingScale";
import FrameworkSection from "./FrameworkSection";
import { LucideTarget, LucideTrendingUp, LucideUsers } from "lucide-react";

const PerformanceFramework = () => {
  const ratingScale = [
    { label: "Exceeds", description: "Performs above expectations. Leads by example and consistently adds extra value.", color: "bg-emerald-500" },
    { label: "Meets", description: "Performs well and consistently meets expectations. Reliable and professional.", color: "bg-blue-500" },
    { label: "Developing", description: "Making progress but still growing skills or consistency in some areas.", color: "bg-orange-500" },
    { label: "Needs Focus", description: "Below expectations. Requires coaching, support, or clear improvement.", color: "bg-red-500" },
  ];

  const ourPeopleCategories = [
    {
      title: "Attitude",
      items: [
        { label: "Exceeds", color: "emerald", text: "Always positive, solution-focused, and encourages others." },
        { label: "Meets", color: "blue", text: "Brings a good attitude and responds well to feedback." },
        { label: "Developing", color: "orange", text: "Occasionally disengaged or reactive." },
        { label: "Needs Focus", color: "red", text: "Negative or resistant; affects team morale." },
      ],
    },
    {
      title: "Communication",
      items: [
        { label: "Exceeds", color: "emerald", text: "Communicates clearly and keeps everyone in the loop." },
        { label: "Meets", color: "blue", text: "Shares updates on time and communicates respectfully." },
        { label: "Developing", color: "orange", text: "Sometimes unclear or slow to communicate." },
        { label: "Needs Focus", color: "red", text: "Poor communication creates confusion or delays." },
      ],
    },
    {
      title: "Initiative",
      items: [
        { label: "Exceeds", color: "emerald", text: "Spots opportunities and takes ownership without being asked." },
        { label: "Meets", color: "blue", text: "Manages tasks well and shows initiative when needed." },
        { label: "Developing", color: "orange", text: "Relies on direction; could take more ownership." },
        { label: "Needs Focus", color: "red", text: "Avoids responsibility or follow-through." },
      ],
    },
    {
      title: "Reliability",
      items: [
        { label: "Exceeds", color: "emerald", text: "Can always be counted on; consistent, punctual, and dependable." },
        { label: "Meets", color: "blue", text: "Generally reliable and delivers on commitments." },
        { label: "Developing", color: "orange", text: "Occasionally late or inconsistent." },
        { label: "Needs Focus", color: "red", text: "Frequently unreliable or unresponsive." },
      ],
    },
  ];

  const ourServiceCategories = [
    {
      title: "Delivery Excellence",
      items: [
        { label: "Exceeds", color: "emerald", text: "Consistently exceeds client expectations and timelines." },
        { label: "Meets", color: "blue", text: "Delivers quality work on time and meets client needs." },
        { label: "Developing", color: "orange", text: "Sometimes misses deadlines or requires follow-up." },
        { label: "Needs Focus", color: "red", text: "Regularly fails to meet commitments." },
      ],
    },
    {
      title: "Quality of Work",
      items: [
        { label: "Exceeds", color: "emerald", text: "Work is exceptional; minimal errors and high attention to detail." },
        { label: "Meets", color: "blue", text: "Produces quality work with acceptable accuracy." },
        { label: "Developing", color: "orange", text: "Occasional quality issues that need correction." },
        { label: "Needs Focus", color: "red", text: "Frequent errors or substandard output." },
      ],
    },
  ];

  const ourGrowthCategories = [
    {
      title: "Adaptability & Learning",
      items: [
        { label: "Exceeds", color: "emerald", text: "Embraces change and actively seeks to learn and improve." },
        { label: "Meets", color: "blue", text: "Adapts to change and is open to new approaches." },
        { label: "Developing", color: "orange", text: "Hesitant to change; needs encouragement." },
        { label: "Needs Focus", color: "red", text: "Resists change or shows no interest in growth." },
      ],
    },
    {
      title: "Problem Solving & Innovation",
      items: [
        { label: "Exceeds", color: "emerald", text: "Brings creative solutions and innovative thinking." },
        { label: "Meets", color: "blue", text: "Solves problems effectively with sound judgment." },
        { label: "Developing", color: "orange", text: "Requires guidance when facing challenges." },
        { label: "Needs Focus", color: "red", text: "Struggles to address problems independently." },
      ],
    },
  ];

  return (
    
    <div className="overflow-y-auto space-y-3">

      <RatingScale items={ratingScale} />

      <FrameworkSection
        icon={LucideUsers}
        color="green"
        title="Our People"
        description="How we connect, collaborate, and contribute to a positive team culture."
        categories={ourPeopleCategories}
      />

      <FrameworkSection
        icon={LucideTarget}
        color="blue"
        title="Our Service"
        description="How we deliver results for our clients."
        categories={ourServiceCategories}
      />

      <FrameworkSection
        icon={LucideTrendingUp}
        color="purple"
        title="Our Growth"
        description="How we learn, adapt, and move forward."
        categories={ourGrowthCategories}
      />
    </div>
  );
};

export default PerformanceFramework;